import axios from "axios";

/**
 * Handoff sécurisé EA (legacy) -> NEA.
 *
 * Le back redirige vers une route React en ajoutant le JWE en dernier segment de path,
 * par exemple /#/chequeculture/create/<jwe>. On récupère ce jeton une seule fois au démarrage,
 * on le range en sessionStorage (il disparaît à la fermeture de l'onglet), on le retire de l'URL,
 * puis on le rejoue en Authorization: Bearer sur chaque appel aux préfixes API protégés
 * (/api/public, /api/private, /api/v2/private, /api/v3/private).
 */

const STORAGE_KEY = "nea.legacy.token";

/**
 * Un JWE compact est fait de 5 parties base64url séparées par des points
 * (header.encryptedKey.iv.ciphertext.tag). On le reconnaît ainsi, ce qui évite de déclarer
 * un paramètre :jwe sur chacune des routes cibles du back.
 */
const JWE_PATTERN = /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]*){4}$/;

export const getJwe = () => sessionStorage.getItem(STORAGE_KEY);

export const clearJwe = () => sessionStorage.removeItem(STORAGE_KEY);

/**
 * Capte le JWE présent en dernier segment du hash, le stocke, et le retire de l'URL
 * pour qu'il ne traîne pas dans l'historique du navigateur ni ne perturbe le routing.
 * À appeler avant le premier rendu.
 */
export const captureJweFromUrl = () => {
  const hash = window.location.hash || "";
  const finQuery = hash.indexOf("?");
  const chemin = finQuery >= 0 ? hash.substring(0, finQuery) : hash;
  const query = finQuery >= 0 ? hash.substring(finQuery) : "";

  const segments = chemin.split("/");
  const dernier = segments[segments.length - 1];
  if (!dernier || !JWE_PATTERN.test(dernier)) {
    return null;
  }

  sessionStorage.setItem(STORAGE_KEY, dernier);
  segments.pop();
  const nouveauHash = segments.join("/") + query;
  window.history.replaceState(null, "", window.location.pathname + window.location.search + nouveauHash);
  return dernier;
};

/** Préfixes protégés côté back : ils exigent tous un Bearer valide, sous peine de 401. */
const estAppelApiNea = (url) => !!url && (
  url.includes("/api"));

/** Ajoute le jeton sur tous les appels axios vers les préfixes API protégés. */
export const installJweInterceptor = () => {
  axios.interceptors.request.use((config) => {
    const jwe = getJwe();
    if (jwe && estAppelApiNea(config.url)) {
      config.headers = { ...config.headers, Authorization: "Bearer " + jwe };
    }
    return config;
  });
};

/** Événement DOM diffusé quand le back rejette un appel protégé pour cause de JWE expiré/invalide. */
export const JWE_EXPIRED_EVENT = "nea:jwe-expired";

/** S'abonne à l'expiration du JWE. Retourne la fonction de désabonnement (à appeler au unmount). */
export const onJweExpired = (callback) => {
  window.addEventListener(JWE_EXPIRED_EVENT, callback);
  return () => window.removeEventListener(JWE_EXPIRED_EVENT, callback);
};

/**
 * Intercepte les réponses 401 des appels protégés qui portaient un Bearer JWE : côté back,
 * LegacyJweBearerFilter ne renvoie 401 sur un appel déjà muni d'un Bearer que si le JWE est
 * indéchiffrable, expiré, ou d'un émetteur inattendu (cf. son javadoc). On le traduit donc ici
 * en expiration de session, et on diffuse un événement global pour que l'app (peu importe le
 * formulaire affiché) puisse afficher le dialog de reconnexion.
 */
export const installJweExpiredInterceptor = () => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const config = error.config || {};
      const status = error.response && error.response.status;
      const bearerEnvoye = !!(config.headers && config.headers.Authorization);
      if (status === 401 && estAppelApiNea(config.url) && bearerEnvoye) {
        clearJwe();
        window.dispatchEvent(new CustomEvent(JWE_EXPIRED_EVENT));
      }
      return Promise.reject(error);
    }
  );
};
